import { Component, computed, effect, ElementRef, inject, input, InputSignal, OnDestroy, signal, Signal, viewChild, WritableSignal } from '@angular/core';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { PlayerService } from '@kinomap-dev/ng-ui-lib/player';
import { MediaBlockService } from '../../services/media-block-service';
import { IconType, TimerPipe } from '@kinomap-dev/ng-ui-lib/core';

@Component({
  selector: 'app-player-controls',
  imports: [IconComponent, TimerPipe],
  template: `
  <div class="progress-bar-container"
    [class.dragging]="isDragging()"
    (mousedown)="onProgressMouseDown($event)">
    <div class="progress-track" #progressTrack>
      <div class="progress-fill" [style.width.%]="progressPercent()"></div>
      <div class="progress-thumb" [style.left.%]="progressPercent()"></div>
    </div>
  </div>
  <div class="actions">
    <button class="play-pause" (click)="togglePlayPause()"><kin-icon color="white" [name]="playPauseIcon()" /></button>
    <button class="move-backward" (click)="_moveBackward()"><kin-icon color="white" name="t15sec_back" /></button>
    <button class="move-forward" (click)="_moveForward()"><kin-icon color="white" name="t15sec_forward" /></button>
    <div class="volume-container" (mouseenter)="showVolume()" (mouseleave)="hideVolume()">
      <button class="volumes-btn" (click)="toggleMute()"><kin-icon color="white" [name]="volumeIcon()" /></button>
      <div class="volume-panel" [class.visible]="volumeHover()">
        <input class="volume-input" type="range" min="0" max="100"
          [value]="volumeLevel()"
          (input)="volumeLevel.set(+$any($event.target).value)" />
      </div>
    </div>
    <p class="time" > {{ playerService.progress() | timer:videoDuration()}} / {{ videoDuration() | timer:videoDuration() }}</p>
    <button class="fullscreen" (click)="_fullscreen()"><kin-icon color="white" name="full_screen" /></button>
  </div>
  `,
  styleUrl: './player-controls.scss',
})
export class PlayerControls implements OnDestroy {

  public playerService: PlayerService = inject(PlayerService);
  mediaBlockService: MediaBlockService = inject(MediaBlockService);
  videoPlayer: InputSignal<ElementRef<HTMLVideoElement>> = input.required<ElementRef<HTMLVideoElement>>();
  progressTrack: Signal<ElementRef<HTMLDivElement> | undefined> = viewChild('progressTrack', { read: ElementRef<HTMLDivElement> });
  volumeLevel = signal<number>(70);
  isDragging: WritableSignal<boolean> = signal(false);
  isPlaying: WritableSignal<boolean> = signal(false);
  playPauseIcon: Signal<IconType> = computed(() => this.isPlaying() ? 'pause' : 'play');

  volumeIcon: Signal<IconType> = computed(() => {
    const volumeLevel: number = this.volumeLevel() / 100;
    if (volumeLevel <= 0) return 'sound_off'
    if (volumeLevel < 0.3) return "sound_on_0"
    if (volumeLevel < 0.7) return "sound_on_1"
    return "sound_on"
  })

  videoDuration: Signal<number> = computed(() => this.mediaBlockService.videoFileData()?.duration ?? 0);

  progressPercent: Signal<number> = computed(() => {
    const duration = this.videoDuration();
    if (!duration) return 0;
    return (this.playerService.progress() / duration) * 100;
  });

  volumeHover: WritableSignal<boolean> = signal<boolean>(false);
  private prevVolumeLevel: number = 70;
  private hideVolumeTimeout: ReturnType<typeof setTimeout> | null = null;

  showVolume(): void {
    if (this.hideVolumeTimeout) {
      clearTimeout(this.hideVolumeTimeout);
      this.hideVolumeTimeout = null;
    }
    this.volumeHover.set(true);
  }

  hideVolume(): void {
    this.hideVolumeTimeout = setTimeout(() => {
      this.volumeHover.set(false);
    }, 400);
  }

  toggleMute(): void {
    if (this.volumeLevel() > 0) {
      this.prevVolumeLevel = this.volumeLevel();
      this.volumeLevel.set(0);
    } else {
      this.volumeLevel.set(this.prevVolumeLevel || 70);
    }
  }

  ngOnDestroy(): void {
    if (this.hideVolumeTimeout) clearTimeout(this.hideVolumeTimeout);
    document.removeEventListener('mousemove', this._onDragMove);
    document.removeEventListener('mouseup', this._onDragEnd);
  }

  constructor() {
    effect(() => {
      this.videoPlayer().nativeElement.volume = this.volumeLevel() / 100;
    });

    effect((onCleanup) => {
      const videoEl = this.videoPlayer().nativeElement;
      const onPlay = () => this.isPlaying.set(true);
      const onPause = () => this.isPlaying.set(false);
      const onEnded = () => this.isPlaying.set(false);
      videoEl.addEventListener('play', onPlay);
      videoEl.addEventListener('pause', onPause);
      videoEl.addEventListener('ended', onEnded);
      onCleanup(() => {
        videoEl.removeEventListener('play', onPlay);
        videoEl.removeEventListener('pause', onPause);
        videoEl.removeEventListener('ended', onEnded);
      });
    });
  }

  _play(): void {
    this.videoPlayer().nativeElement.play();
  }

  _pause(): void {
    this.videoPlayer().nativeElement.pause();
  }

  _moveForward(): void {
    this.navigateOnVideo('forward', 15)
  }

  _moveBackward(): void {
    this.navigateOnVideo('backward', 15)
  }

  _fullscreen(): void {
    this.videoPlayer().nativeElement.requestFullscreen().then(() => { })
  }

  onProgressMouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.isDragging.set(true);
    this._seekFromClientX(event.clientX);
    document.addEventListener('mousemove', this._onDragMove);
    document.addEventListener('mouseup', this._onDragEnd);
  }

  private _onDragMove = (event: MouseEvent): void => {
    this._seekFromClientX(event.clientX);
  };

  private _onDragEnd = (): void => {
    this.isDragging.set(false);
    document.removeEventListener('mousemove', this._onDragMove);
    document.removeEventListener('mouseup', this._onDragEnd);
  };

  private _seekFromClientX(clientX: number): void {
    const track = this.progressTrack()?.nativeElement;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    this.updateVideoTime(ratio * this.videoDuration());
  }

  togglePlayPause(): void {
    const videoEl: HTMLVideoElement = this.videoPlayer().nativeElement;
    if (videoEl.paused || videoEl.ended) {
      this.isPlaying.set(true);
      videoEl.play();
    } else {
      this.isPlaying.set(false);
      videoEl.pause();
    }
  }

  updateVideoTime(time: number): void {
    const progress: number = parseFloat(time.toFixed(2));
    const videoElement: HTMLVideoElement = this.videoPlayer().nativeElement;
    videoElement.currentTime = progress;
  }

  private navigateOnVideo(direction: 'forward' | 'backward', time: number): void {
    const currentProgress: number = this.playerService.progress();
    const nextProgress: number =
      direction === 'backward'
        ? Math.max(currentProgress - time, 0)
        : currentProgress + time;
    this.updateVideoTime(nextProgress);
  }
}
